/**
 * GameManager类
 * 游戏主控制器，管理游戏逻辑和状态
 */

import type { GameConfig, GameState, Position, TileValue } from './types';
import { Direction, GameState as GameStateEnum } from './types';
import { Grid } from './grid';
import { Tile } from './tile';
import { HTMLView } from './html-view';
import { InputManager } from './input-manager';
import { StorageManager } from './storage-manager';
import {
  GRID_SIZE,
  START_TILES,
  WIN_VALUE,
  TILE_2_PROBABILITY,
} from './constants';
import { randomChoice } from './utils';

export class GameManager {
  private size: number;
  private startTiles: number;
  private grid: Grid;
  private score: number;
  private over: boolean;
  private won: boolean;
  private keepPlaying: boolean;

  private view: HTMLView;
  private inputManager: InputManager;
  private storageManager: StorageManager;

  /**
   * 构造函数
   * @param config 游戏配置
   * @param view 视图实例
   * @param inputManager 输入管理器实例
   * @param storageManager 存储管理器实例
   */
  constructor(
    config: GameConfig = { size: GRID_SIZE, startTiles: START_TILES },
    view: HTMLView,
    inputManager: InputManager,
    storageManager: StorageManager
  ) {
    this.size = config.size;
    this.startTiles = config.startTiles;
    this.grid = new Grid(this.size);
    this.score = 0;
    this.over = false;
    this.won = false;
    this.keepPlaying = false;

    this.view = view;
    this.inputManager = inputManager;
    this.storageManager = storageManager;

    // 绑定输入事件
    this.inputManager.on('move', this.move.bind(this));
    this.inputManager.on('restart', this.restart.bind(this));
  }

  /**
   * 初始化游戏
   */
  public setup(): void {
    // 尝试从本地存储恢复游戏状态
    const previousState = this.storageManager.getGameState();

    if (previousState) {
      // 恢复游戏状态
      this.grid = new Grid(previousState.grid.size);
      previousState.grid.cells.forEach((cellData) => {
        if (cellData) {
          const tile = Tile.deserialize(cellData);
          this.grid.insertTile(tile);
        }
      });
      this.score = previousState.score;
      this.over = previousState.over;
      this.won = previousState.won;
    } else {
      // 新游戏
      this.grid.empty();
      this.score = 0;
      this.over = false;
      this.won = false;
      this.keepPlaying = false;

      // 添加初始方块
      this.addStartTiles();
    }

    // 更新视图
    this.actuate();
  }

  /**
   * 添加初始方块
   */
  private addStartTiles(): void {
    for (let i = 0; i < this.startTiles; i++) {
      this.addRandomTile();
    }
  }

  /**
   * 添加随机方块
   * @returns 是否成功添加
   */
  public addRandomTile(): boolean {
    const availableCells = this.grid.availableCells();
    if (availableCells.length === 0) {
      return false;
    }

    const position = randomChoice(availableCells);
    if (!position) {
      return false;
    }

    // 90%概率是2，10%概率是4
    const value: TileValue = Math.random() < TILE_2_PROBABILITY ? 2 : 4;
    const tile = new Tile(position, value);

    this.grid.insertTile(tile);
    return true;
  }

  /**
   * 处理移动操作
   * @param direction 移动方向
   */
  public move(direction: Direction): void {
    if (this.isGameTerminated()) {
      return;
    }

    let moved = false;
    const vector = this.getVector(direction);

    // 保存方块位置
    this.prepareTiles();

    // 按移动方向遍历网格
    const traversals = this.buildTraversals(vector);

    // 遍历所有方块并移动
    traversals.x.forEach((x) => {
      traversals.y.forEach((y) => {
        const position: Position = { x, y };
        const tile = this.grid.cellContent(position);

        if (tile) {
          const positions = this.findFarthestPosition(position, vector);
          const next = this.grid.cellContent(positions.next);

          // 检查是否可以合并
          if (next && next.value === tile.value && !next.mergedFrom) {
            // 合并方块
            const merged = new Tile(positions.next, (tile.value * 2) as TileValue);
            merged.mergedFrom = [tile, next];

            this.grid.insertTile(merged);
            this.grid.removeTile(tile);

            // 更新位置
            tile.updatePosition(positions.next);

            // 更新分数
            this.score += merged.value;

            // 检查是否获胜
            if (merged.value === WIN_VALUE) {
              this.won = true;
            }

            moved = true;
          } else {
            // 移动到最远位置
            this.moveTile(tile, positions.farthest);
            if (tile.position.x !== position.x || tile.position.y !== position.y) {
              moved = true;
            }
          }
        }
      });
    });

    if (moved) {
      this.addRandomTile();

      if (!this.movesAvailable()) {
        this.over = true;
      }

      this.actuate();
    }
  }

  /**
   * 准备方块（保存位置）
   */
  private prepareTiles(): void {
    this.grid.eachCell((_x, _y, tile) => {
      if (tile) {
        tile.mergedFrom = undefined;
        tile.savePosition();
      }
    });
  }

  /**
   * 移动方块
   * @param tile 方块
   * @param position 目标位置
   */
  private moveTile(tile: Tile, position: Position): void {
    this.grid.removeTile(tile);
    tile.updatePosition(position);
    this.grid.insertTile(tile);
  }

  /**
   * 获取方向向量
   * @param direction 方向
   * @returns 向量
   */
  private getVector(direction: Direction): Position {
    const map = {
      [Direction.Up]: { x: 0, y: -1 },
      [Direction.Down]: { x: 0, y: 1 },
      [Direction.Left]: { x: -1, y: 0 },
      [Direction.Right]: { x: 1, y: 0 },
    };
    return map[direction];
  }

  /**
   * 构建遍历顺序
   * @param vector 方向向量
   * @returns 遍历顺序
   */
  private buildTraversals(vector: Position): { x: number[]; y: number[] } {
    const traversals: { x: number[]; y: number[] } = { x: [], y: [] };

    for (let pos = 0; pos < this.size; pos++) {
      traversals.x.push(pos);
      traversals.y.push(pos);
    }

    // 如果向右或向下移动，需要反向遍历
    if (vector.x === 1) traversals.x.reverse();
    if (vector.y === 1) traversals.y.reverse();

    return traversals;
  }

  /**
   * 查找最远可移动位置
   * @param position 当前位置
   * @param vector 方向向量
   * @returns 最远位置和下一个位置
   */
  private findFarthestPosition(
    position: Position,
    vector: Position
  ): { farthest: Position; next: Position } {
    let previous: Position;

    do {
      previous = position;
      position = { x: previous.x + vector.x, y: previous.y + vector.y };
    } while (this.grid.cellAvailable(position));

    return {
      farthest: previous,
      next: position,
    };
  }

  /**
   * 检查是否还有可用移动
   * @returns 是否有可用移动
   */
  private movesAvailable(): boolean {
    return this.grid.cellsAvailable() || this.tileMatchesAvailable();
  }

  /**
   * 检查是否有可合并的方块
   * @returns 是否有可合并的方块
   */
  private tileMatchesAvailable(): boolean {
    for (let x = 0; x < this.size; x++) {
      for (let y = 0; y < this.size; y++) {
        const tile = this.grid.cellContent({ x, y });

        if (tile) {
          // 检查相邻方块
          const directions: Position[] = [
            { x: 0, y: -1 }, // 上
            { x: 1, y: 0 }, // 右
            { x: 0, y: 1 }, // 下
            { x: -1, y: 0 }, // 左
          ];

          for (const direction of directions) {
            const other = this.grid.cellContent({
              x: x + direction.x,
              y: y + direction.y,
            });

            if (other && other.value === tile.value) {
              return true;
            }
          }
        }
      }
    }

    return false;
  }

  /**
   * 检查游戏是否结束
   * @returns 是否结束
   */
  private isGameTerminated(): boolean {
    return this.over || (this.won && !this.keepPlaying);
  }

  /**
   * 重新开始游戏
   */
  public restart(): void {
    this.storageManager.clearGameState();
    this.view.hideMessage();
    this.setup();
  }

  /**
   * 继续游戏（获胜后）
   */
  public keepPlayingAfterWin(): void {
    this.keepPlaying = true;
    this.view.hideMessage();
  }

  /**
   * 更新视图和存储
   */
  private actuate(): void {
    // 更新最高分
    if (this.storageManager.updateBestScore(this.score)) {
      this.view.updateBestScore(this.score);
    } else {
      this.view.updateBestScore(this.storageManager.getBestScore());
    }

    // 更新分数
    this.view.updateScore(this.score);

    // 渲染网格
    const tiles = this.grid.getAllTiles();
    this.view.render(tiles);

    // 保存游戏状态
    this.storageManager.setGameState({
      grid: this.grid.serialize(),
      score: this.score,
      over: this.over,
      won: this.won,
    });

    // 显示游戏结束消息
    if (this.over) {
      this.view.showMessage('GAME OVER', 'lose');
    } else if (this.won && !this.keepPlaying) {
      this.view.showMessage('恭喜你赢了！', 'win');
    }
  }

  /**
   * 获取当前分数
   * @returns 分数
   */
  public getScore(): number {
    return this.score;
  }

  /**
   * 获取游戏状态
   * @returns 游戏状态
   */
  public getState(): GameState {
    if (this.over) {
      return GameStateEnum.Over;
    } else if (this.won) {
      return GameStateEnum.Won;
    } else {
      return GameStateEnum.Playing;
    }
  }
}
